/* eslint-disable no-nested-ternary */
import React, { Component, Fragment } from 'react';
import _ from 'lodash';
import moment from 'moment-timezone';

import { humanize } from 'common/helpers';
import { getPreferredTimezone } from 'common/helpers/time';
import Loading from '../../../../../common/components/atoms/Loading';
import * as utils from '../../../../../common/utils';

import NoDataChart from './NoDataChart';
import OverlayTraceLegend from './OverlayTraceLegend';
import TagChartView from './TagChartView';
import {
  shouldShowOutlierModal,
  buildSelectionState,
  getRulDatapoints,
  getInitialSelectionPoint,
  getSelectionAtBrushEnd,
  findSelectionIndex,
  getNextPointsOnClick
} from './tagChartSelectionUtils';
import {
  deriveOverlayChartData,
  isOverlayAllTracesFailed,
  getTagChartOverlayContext
} from './overlayChartDataUtils';
import * as modalHandlers from './tagChartModalHandlers';
import {
  tagChartPropTypes,
  tagChartDefaultProps,
  withTagChartRedux
} from './tagChartRedux';

class TagChart extends Component {
  static getDerivedStateFromProps(nextProps, prevState) {
    if (nextProps.points && !_.isEqual(nextProps.points, prevState.points)) {
      return {
        points: nextProps.points
      };
    }

    return null;
  }

  constructor(props) {
    super(props);
    this.mouseoverChart = this.mouseoverChart.bind(this);
    this.mouseClick = this.mouseClick.bind(this);
    this.brushCb = this.brushCb.bind(this);
    this.addNote = this.addNote.bind(this);
    this.state = {
      selection: {},
      points: [],
      dataPos: {
        y: false,
        x: false
      },
      dataPoint: null,
      showSettingsModal: false,
      rulFdpDatapoint: {},
      rulStartTimeDatapoint: {},
      hoveredLegendItem: null,
      activeLegendItem: null
    };
  }

  componentDidMount() {
    const { data, loading } = this.props;
    if (!loading && data) this.setInitialSelection();
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      !this.props.loading &&
      (!_.isEqual(this.props.data, prevProps.data) ||
        ((`${this.props.yUnit}` !== this.state.selection.yUnit) && this.state.selection.yUnit !== 'dB') ||
        (this.props.warningThreshold && (this.props.warningThreshold !== prevProps.warningThreshold) ||
          this.props.criticalThreshold && (this.props.criticalThreshold !== prevProps.criticalThreshold)) ||
        (this.props.rulFdp !== prevProps.rulFdp) || (this.props.rulStartTime !== prevProps.rulStartTime)
      )
    ) {
      this.setInitialSelection(prevState.selectedIdx);
    }
    const isSpectrum = this.props.type === 'spectrum' || this.props.type === 'demod_spectrum';
    if (isSpectrum ? !_.isEqual(this.props.originalData, prevProps.originalData) : !_.isEqual(this.props.data, prevProps.data)) {
      this.setState({
        points: []
      });
    }
    if (this.props.skipOverlay) {
      this.addNote();
    }

    if (!this.shouldShowOutlierModal() && this.state.showSettingsModal) {
      this.setState({ showSettingsModal: false });
    }
  }

  storeBrushDebounce = _.debounce(
    this.props.storeBrushSelection ?
      () => this.props.storeBrushSelection(this.state.brushSelection) :
      () => { },
    500
  )

  setPointsToCorrectUnits = (points) => {
    this.setState({ points });
  }

  getAxisProps = () => {
    const { yTitle, yUnit, xTitle, xUnit, xIsDate } = this.props;
    return { xTitle, xUnit, xIsDate, yTitle, yUnit };
  }

  shouldShowOutlierModal = () => shouldShowOutlierModal(this.props);

  closeSettingsModal = () => {
    this.setState({ showSettingsModal: false });
  }

  getModalHandlerArgs = () => ({
    tagId: this.props.tagId,
    dataPoint: this.state.dataPoint,
    componentId: this.props.componentId,
    machineId: this.props.machineId,
    siteId: this.props.siteId,
    machineChartsActions: this.props.machineChartsActions,
    rulActions: this.props.rulActions,
    refreshChartAfterOutlier: this.props.refreshChartAfterOutlier,
    onClose: this.closeSettingsModal
  });

  markReadingAsOutlier = () => {
    modalHandlers.markReadingAsOutlier(this.getModalHandlerArgs());
  }

  deleteOutlierReading = () => {
    modalHandlers.deleteOutlierReading(this.getModalHandlerArgs());
  }

  setAsFDP = () => {
    modalHandlers.setAsFDP(this.getModalHandlerArgs());
  }

  deleteFDP = () => {
    modalHandlers.deleteFDP(this.getModalHandlerArgs());
  }

  setAsStartTime = () => {
    modalHandlers.setAsStartTime(this.getModalHandlerArgs());
  }

  deleteStartTime = () => {
    modalHandlers.deleteStartTime(this.getModalHandlerArgs());
  }

  setSelectionToBrushEnd = () => {
    if (this.props.noHover) return;
    const { data } = this.props;
    const { brushSelection } = this.state;
    const selection = getSelectionAtBrushEnd(data, brushSelection, this.getAxisProps());
    if (selection) {
      this.setState({ selection });
    }
  };

  brushCb(selection) {
    this.setState({
      brushSelection: selection
    }, () => {
      this.storeBrushDebounce();
      this.setSelectionToBrushEnd();
    });
  }

  setInitialSelection(oldSlectedIdx = undefined) {
    if (this.props.noHover || !this.props.data) return;
    const { data, rulFdp, rulStartTime } = this.props;
    const selection = getInitialSelectionPoint(data, oldSlectedIdx);
    const { rulFdpDatapoint, rulStartTimeDatapoint } = getRulDatapoints(data, rulFdp, rulStartTime);

    if (data.length) {
      this.setState({
        selection: buildSelectionState(selection, this.getAxisProps()),
        rulFdpDatapoint,
        rulStartTimeDatapoint
      });
    }
  }

  mouseoverChart(selection) {
    if (this.props.mouseoverChart) {
      this.props.mouseoverChart(selection);
    }
    this.setState({
      selection: buildSelectionState(selection, this.getAxisProps()),
    });
    if (this.props.setLastHoveredChart) {
      this.props.setLastHoveredChart(this.props.type);
    }
  }

  mouseClick(selection = undefined, event) {
    const idx = findSelectionIndex(this.props.data, this.state.selection.x);
    if (this.props.mouseClick) {
      if (!selection && (this.state.selection.x === this.state.points.x)) {
        this.setState({
          showSettingsModal: this.shouldShowOutlierModal()
        });
      } else {
        this.props.mouseClick(selection || this.state.selection, event);
        if (idx !== undefined) {
          this.setState({
            selectedIdx: idx,
            showSettingsModal: false
          });
          if (this.props.setSelectedIdx) this.props.setSelectedIdx(idx);
        }
      }
    } else {
      this.setState(prevState => ({
        points: getNextPointsOnClick(prevState.points, prevState.selection)
      }));
    }
  }

  setDataPosAndDataPoint = (dataPos, dataPoint) => {
    this.setState({
      dataPos,
      dataPoint,
      showSettingsModal: this.shouldShowOutlierModal()
    });
  }

  addNote() {
    this.props.addNote({
      title: this.props.title,
      type: this.props.type,
      start: this.state.brushSelection ? this.state.brushSelection[0] : undefined,
      end: this.state.brushSelection ? this.state.brushSelection[1] : undefined,
      data: this.props.data,
      xAxisType: this.props.xAxisType,
      tag_id: this.props.tagId
    });
  }

  getChartRange = () => {
    const { data, spectralEnvelope, unit_system, tag_type, type, title, yUnit } = this.props;
    const range = utils.chartRange(data, unit_system, yUnit, title, tag_type, type);
    if (spectralEnvelope && spectralEnvelope.envelope_data && !_.isEmpty(spectralEnvelope.envelope_data.start_frequencies)) {
      const envelope_range = utils.getStepGraphRange(spectralEnvelope.envelope_data);
      range[0] = Math.min(range[0], envelope_range[0]);
      range[1] = Math.max(range[1], envelope_range[1]);
    }
    return range;
  }

  getYLabel = () => {
    const { tag_type, yTitle, yUnit, type, showOverlay, selectedPoints } = this.props;

    let currentYUnit = yUnit;
    if (showOverlay && selectedPoints && selectedPoints.length > 0) {
      const pointWithYUnit = selectedPoints.find(p => p.yUnit);
      if (pointWithYUnit && pointWithYUnit.yUnit) {
        currentYUnit = pointWithYUnit.yUnit;
      }
    }

    if (tag_type === 'vibration' && (type === 'spectrum' || type === 'demod_spectrum')) {
      return `${yTitle} ${currentYUnit === '' ? '' : `(${currentYUnit},0-Pk)`}`;
    }
    return `${yTitle} ${currentYUnit === '' ? '' : `(${currentYUnit})`}`;
  }

  getTimezoneAndProps = () => {
    const { user, currentAccount } = this.props;
    const timezone = getPreferredTimezone(user, currentAccount) || moment.tz.guess();
    return { timezone };
  };

  getOverlayContext = () => getTagChartOverlayContext(this.props, {
    onHoveredLegendItemChange: index => this.setState({ hoveredLegendItem: index }),
    onActiveLegendItemChange: index => this.setState({ activeLegendItem: index })
  }, {
    hoveredLegendItem: this.state.hoveredLegendItem,
    activeLegendItem: this.state.activeLegendItem
  });

  render() {
    const {
      data,
      tagId,
      loading,
      title,
      isCepstrum,
      yUnit,
      expanded,
      xAxisType,
      xUnit,
      initialXunit,
      addingNote,
      config_id,
      displayOutliers,
      color,
      skipOverlay,
      type,
      filterSettings,
      shaftSpeed,
      originalData,
      warningThreshold,
      criticalThreshold,
      showOverlay,
      selectedPoints,
      colors,
      activeData
    } = this.props;

    const { t } = this.props;
    const {
      isTraceOverlay,
      traceDataKey,
      overlayLegendPoints,
      validPoints,
      overlayLoadingState,
      getChartLineIndex,
      getOverlayLegendProps
    } = this.getOverlayContext();
    const { showOverlayLoadingOverlay } = overlayLoadingState;
    const overlayChartHeight = this.props.height || '300px';

    if (!data && loading && !showOverlayLoadingOverlay) {
      return (
        <Loading center />
      );
    }

    if (showOverlayLoadingOverlay && isTraceOverlay) {
      const { timezone } = this.getTimezoneAndProps();
      return (
        <OverlayTraceLegend
          {...getOverlayLegendProps([], [], timezone)}
          loadingPlaceholder
          chartHeight={overlayChartHeight}
          optionsVisible={!!this.props.children}
        />
      );
    }

    if (data) {
      const range = this.getChartRange();
      const toFixedDecimalPoints = type === 'demod_spectrum' ? 4 : 2;
      const overlayAllTracesFailed = isOverlayAllTracesFailed({
        isTraceOverlay,
        isOverlayTraceLoading: overlayLoadingState.isOverlayTraceLoading,
        overlayLegendPoints,
        traceDataKey
      });

      const { chartData, chartColors } = deriveOverlayChartData({
        isTraceOverlay,
        validPoints,
        traceDataKey,
        colors,
        color,
        data
      });

      const { timezone } = this.getTimezoneAndProps();

      if (!chartData || !chartData.length) {
        if (isTraceOverlay && overlayAllTracesFailed) {
          return (
            <Fragment>
              <NoDataChart
                configId={`config_id-${type}-overlay`}
                height="200px"
                title={t('charts.overlay.allMeasurementsFailed')}
              />
              <OverlayTraceLegend
                {...getOverlayLegendProps([], [], timezone)}
              />
            </Fragment>
          );
        }

        const fallbackType = isCepstrum ? title : humanize(type);
        return (
          <NoDataChart
            configId={`config_id-${type}`}
            height="200px"
            title={t('machineDashboard.health.charts.noDataForType', { type: fallbackType })}
          />
        );
      }

      const showOutlierModal = displayOutliers &&
        !this.props.editRULSettings &&
        this.state.showSettingsModal &&
        this.state.dataPos.x !== false &&
        this.shouldShowOutlierModal();

      return (
        <TagChartView
          title={title}
          type={type}
          isCepstrum={isCepstrum}
          showOverlay={showOverlay}
          filterSettings={filterSettings}
          selection={this.state.selection}
          toFixedDecimalPoints={toFixedDecimalPoints}
          shaftSpeed={shaftSpeed}
          initialXunit={initialXunit}
          annotationsVisible={this.props.annotationsVisible}
          loading={loading}
          showOverlayLoadingOverlay={showOverlayLoadingOverlay}
          loadingMessage={t('charts.overlay.loadingMeasurements')}
          chartProps={{
            ...this.props,
            chartName: this.props.chartName || `tag-${tagId}-${title}-${config_id}`,
            originalData: [originalData],
            outliersArray: data.filter(d => d.is_outlier),
            range,
            contextChart: expanded,
            mouseover: this.mouseoverChart,
            selection: this.state.selection,
            selectedRange: this.props.selectedRange,
            height: this.props.height ? this.props.height : '100px',
            ylabel: this.getYLabel(),
            xAxis: xAxisType,
            xUnit,
            yUnit,
            isCepstrum,
            setPointsToCorrectUnits: this.setPointsToCorrectUnits,
            mouseClick: this.mouseClick,
            points: this.state.points,
            warningThreshold,
            criticalThreshold,
            brushCb: this.brushCb,
            setDataPosAndDataPoint: this.setDataPosAndDataPoint,
            rulFdpDatapoint: this.state.rulFdpDatapoint,
            rulStartTimeDatapoint: this.state.rulStartTimeDatapoint,
            hoveredLegendItem: getChartLineIndex(this.state.hoveredLegendItem),
            activeLegendItem: getChartLineIndex(this.state.activeLegendItem),
            selectedPoints,
            activeData
          }}
          chartData={chartData}
          chartColors={chartColors}
          addingNote={addingNote}
          skipOverlay={skipOverlay}
          onAddNote={this.addNote}
          showOutlierModal={showOutlierModal}
          dataPos={this.state.dataPos}
          dataPoint={this.state.dataPoint}
          onCloseOutlierModal={this.closeSettingsModal}
          markReadingAsOutlier={this.markReadingAsOutlier}
          deleteOutlierReading={this.deleteOutlierReading}
          isTraceOverlay={isTraceOverlay}
          overlayLegendProps={getOverlayLegendProps(chartData, chartColors, timezone)}
        >
          {this.props.children}
        </TagChartView>
      );
    }
    const fallbackType = isCepstrum ? title : humanize(type);
    return (
      <NoDataChart
        configId={`config_id-${type}`}
        height="200px"
        title={t('machineDashboard.health.charts.noDataForType', { type: fallbackType })}
      />
    );
  }
}

TagChart.propTypes = tagChartPropTypes;
TagChart.defaultProps = tagChartDefaultProps;

export default withTagChartRedux(TagChart);
